/**
 * Which room a room code means.
 *
 * A room is found by its 4-letter code, and a code is free again the moment its
 * room is deleted (26^4 = 456,976 codes). Anything that remembers a room by its
 * code alone - the "Return to lobby" button, Recently Joined, a friend invite,
 * a shared link, a bookmark - will therefore, once the code is reused, drop the
 * player into a stranger's room that was never made for them.
 *
 * A room's identity is its `createdAt`: written once by whoever creates it and
 * never rewritten. A reference to a room is a server time at which the room it
 * means already existed - that room's own createdAt for our records and links,
 * the invite's createdAt for a friend invite. The room holding the code now is
 * a different room exactly when it was created after that moment.
 */

export const LOBBY_REF_PARAM = 'r';

/** This device's memory of the rooms it has been in: code -> room createdAt. */
const VISITS_STORAGE_KEY = 'gnc_lobby_visits';
const MAX_REMEMBERED_VISITS = 40;

/**
 * Rooms visited before visits were recorded left only `lastLobbyId` and
 * `lastLobbyTimestamp`, a CLIENT clock. A wrong device clock must not make the
 * player's own room look new, so that record only calls a room different when
 * it was created well after the last visit. Private rooms are deleted weeks
 * after their last visit, so the margin costs nothing where it matters.
 */
export const LEGACY_VISIT_CLOCK_MARGIN_MS = 12 * 60 * 60 * 1000;

/** Set by the join form: the player typed this code and wants whatever room holds it now. */
const CODE_ENTERED_STORAGE_KEY = 'gnc_lobby_code_entered';
export const CODE_ENTERED_TTL_MS = 10 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStorage(kind: 'local' | 'session'): StorageLike | null {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function positiveMillis(ms: unknown): number | null {
  return typeof ms === 'number' && Number.isFinite(ms) && ms > 0 ? Math.floor(ms) : null;
}

/** Firestore Timestamp, `{ seconds, nanoseconds }`, Date or epoch ms -> epoch ms. */
export function toLobbyMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return positiveMillis(value);
  if (value instanceof Date) return positiveMillis(value.getTime());
  if (typeof value === 'object') {
    const timestamp = value as { toMillis?: unknown; seconds?: unknown; nanoseconds?: unknown };
    if (typeof timestamp.toMillis === 'function') {
      try {
        return positiveMillis((timestamp.toMillis as () => number)());
      } catch {
        return null;
      }
    }
    if (typeof timestamp.seconds === 'number') {
      const nanos = typeof timestamp.nanoseconds === 'number' ? timestamp.nanoseconds : 0;
      return positiveMillis(timestamp.seconds * 1000 + Math.floor(nanos / 1e6));
    }
  }
  return null;
}

/** Null while the creator's serverTimestamp is still pending, or on a room without one. */
export function getLobbyCreatedAtMs(lobby: { createdAt?: unknown } | null | undefined): number | null {
  return toLobbyMillis(lobby?.createdAt);
}

export function encodeLobbyRef(ms: number | null | undefined): string | null {
  const value = positiveMillis(ms);
  return value == null ? null : value.toString(36);
}

export function decodeLobbyRef(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || !/^[0-9a-z]{1,11}$/i.test(value)) return null;
  return positiveMillis(parseInt(value, 36));
}

/** Adds `r=<ref>` to a lobby link; the link is returned unchanged when there is no reference. */
export function withLobbyRef(href: string, refMs: number | null | undefined): string {
  const ref = encodeLobbyRef(refMs);
  if (!ref) return href;
  return `${href}${href.includes('?') ? '&' : '?'}${LOBBY_REF_PARAM}=${ref}`;
}

export function buildLobbyHref(code: string, refMs?: number | null): string {
  return withLobbyRef(`/lobby/${code}`, refMs);
}

/**
 * True only when both times are known and the room holding the code was
 * created after the moment the reference was taken. Unknown on either side
 * means "cannot tell", which keeps today's behaviour.
 */
export function isDifferentLobby(lobbyCreatedAtMs: number | null, refMs: number | null): boolean {
  return lobbyCreatedAtMs != null && refMs != null && lobbyCreatedAtMs > refMs;
}

export type ClosedRoomReason = 'link' | 'visit';

export interface ClosedRoomCheckInput {
  lobby: {
    createdAt?: unknown;
    hostId?: string | null;
    originalHostId?: string | null;
    players?: Record<string, unknown> | null;
  } | null | undefined;
  uid: string | null | undefined;
  /** `r` from the URL: the link names the room it was made for. */
  linkRefMs: number | null;
  /** This device's memory of the code (readLobbyVisitRef). */
  visitRefMs: number | null;
  /**
   * The player asked for whatever room holds the code now: typed it into the
   * join form, picked it from a live list, or chose "Join the new room".
   */
  freshIntent: boolean;
}

/**
 * Why the room behind this code is not the room the player is trying to get
 * back to, or null when it is (or when there is no way to tell).
 */
export function getClosedRoomReason(input: ClosedRoomCheckInput): ClosedRoomReason | null {
  const { lobby, uid } = input;
  if (!lobby || !uid) return null;
  // Seated in it, hosting it or owning it: this is the player's room.
  if (lobby.players?.[uid] || lobby.hostId === uid || lobby.originalHostId === uid) return null;

  const createdAtMs = getLobbyCreatedAtMs(lobby);
  if (createdAtMs == null) return null;

  // A link that names its room is the strongest evidence either way: a fresh
  // invite for this room joins even if this device remembers an older one.
  if (input.linkRefMs != null) return isDifferentLobby(createdAtMs, input.linkRefMs) ? 'link' : null;
  if (input.freshIntent) return null;
  return isDifferentLobby(createdAtMs, input.visitRefMs) ? 'visit' : null;
}

function readVisits(storage: StorageLike): Record<string, number> {
  try {
    const raw = storage.getItem(VISITS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const visits: Record<string, number> = {};
    for (const [code, value] of Object.entries(parsed as Record<string, unknown>)) {
      const ms = positiveMillis(value);
      if (ms != null) visits[code] = ms;
    }
    return visits;
  } catch {
    return {};
  }
}

/** Records that this device was in the room with this code and createdAt. */
export function rememberLobbyVisit(
  code: string,
  createdAtMs: number | null,
  storage: StorageLike | null = browserStorage('local'),
): void {
  const createdAt = positiveMillis(createdAtMs);
  if (!storage || !code || createdAt == null) return;
  const key = code.toUpperCase();
  const visits = readVisits(storage);
  // Runs on every lobby snapshot; only write when the room changed.
  if (visits[key] === createdAt) return;
  delete visits[key];
  const next = [[key, createdAt] as const, ...Object.entries(visits)].slice(0, MAX_REMEMBERED_VISITS);
  try {
    storage.setItem(VISITS_STORAGE_KEY, JSON.stringify(Object.fromEntries(next)));
  } catch {
    // Storage full or blocked: the room still works, it just is not remembered.
  }
}

/** The reference this device holds for a code, or null when it never recorded one. */
export function readLobbyVisitRef(
  code: string,
  storage: StorageLike | null = browserStorage('local'),
): number | null {
  if (!storage || !code) return null;
  const key = code.toUpperCase();
  const visit = readVisits(storage)[key];
  if (visit) return visit;
  try {
    if ((storage.getItem('lastLobbyId') || '').toUpperCase() !== key) return null;
    const lastVisitMs = positiveMillis(Number(storage.getItem('lastLobbyTimestamp')));
    return lastVisitMs == null ? null : lastVisitMs + LEGACY_VISIT_CLOCK_MARGIN_MS;
  } catch {
    return null;
  }
}

export function markLobbyCodeEntered(
  code: string,
  nowMs: number = Date.now(),
  storage: StorageLike | null = browserStorage('session'),
): void {
  if (!storage || !code) return;
  try {
    storage.setItem(CODE_ENTERED_STORAGE_KEY, JSON.stringify({ code: code.toUpperCase(), at: nowMs }));
  } catch {
    // Without the mark the player may be asked once more; nothing breaks.
  }
}

export function wasLobbyCodeJustEntered(
  code: string,
  nowMs: number = Date.now(),
  storage: StorageLike | null = browserStorage('session'),
): boolean {
  if (!storage || !code) return false;
  try {
    const raw = storage.getItem(CODE_ENTERED_STORAGE_KEY);
    if (!raw) return false;
    const entered = JSON.parse(raw) as { code?: unknown; at?: unknown };
    const age = typeof entered?.at === 'number' ? nowMs - entered.at : NaN;
    return entered?.code === code.toUpperCase() && age >= 0 && age < CODE_ENTERED_TTL_MS;
  } catch {
    return false;
  }
}

const removedStorageKey = (code: string) => `kicked_${code.toUpperCase()}`;

/**
 * Remembers that the player was taken out of THIS room, so the join effect
 * does not put them straight back. The value is the room's createdAt, so a new
 * room that reuses the code is not refused because of the old one.
 */
export function markRemovedFromLobby(
  code: string,
  createdAtMs: number | null,
  storage: StorageLike | null = browserStorage('local'),
): void {
  if (!storage || !code) return;
  const createdAt = positiveMillis(createdAtMs);
  try {
    storage.setItem(removedStorageKey(code), createdAt == null ? 'true' : String(createdAt));
  } catch {
    // Best effort, as before.
  }
}

export function clearRemovedFromLobby(
  code: string,
  storage: StorageLike | null = browserStorage('local'),
): void {
  if (!storage || !code) return;
  try {
    storage.removeItem(removedStorageKey(code));
  } catch {
    // Best effort, as before.
  }
}

export function isRemovedFromLobby(
  code: string,
  createdAtMs: number | null,
  options: { explicitJoin?: boolean; visitRefMs?: number | null } = {},
  storage: StorageLike | null = browserStorage('local'),
): boolean {
  if (!storage || !code) return false;
  let value: string | null = null;
  try {
    value = storage.getItem(removedStorageKey(code));
  } catch {
    return false;
  }
  if (!value) return false;
  if (value !== 'true') {
    const removedFromMs = positiveMillis(Number(value));
    // A room the mark cannot be matched against keeps it, as before.
    return removedFromMs == null || createdAtMs == null || removedFromMs === createdAtMs;
  }
  // Written before the mark named its room. A code typed on purpose is asking
  // for the room there now (a real kick is still refused by the room's own
  // kickedUids); otherwise keep it unless the room is known to be newer.
  if (options.explicitJoin) return false;
  return !isDifferentLobby(createdAtMs, options.visitRefMs ?? null);
}
